import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { startEloVoting, submitEloVote, type EloPhoto } from '../lib/api'

export default function EloVotingPage() {
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [photoA, setPhotoA] = useState<EloPhoto | null>(null)
  const [photoB, setPhotoB] = useState<EloPhoto | null>(null)
  const [comparisonsCompleted, setComparisonsCompleted] = useState(0)
  const [comparisonsRequired, setComparisonsRequired] = useState(25)
  const [isComplete, setIsComplete] = useState(false)

  // Track which photo was selected for animation
  const [selectedPhoto, setSelectedPhoto] = useState<'A' | 'B' | null>(null)

  useEffect(() => {
    async function initSession() {
      setIsLoading(true)
      setError(null)

      const result = await startEloVoting()

      if (result.error) {
        setError(result.error)
        setIsLoading(false)
        return
      }

      if (result.data) {
        setSessionId(result.data.sessionId)
        setPhotoA(result.data.photoA)
        setPhotoB(result.data.photoB)
        setComparisonsRequired(result.data.progress.total)
        setComparisonsCompleted(result.data.progress.completed)
      }

      setIsLoading(false)
    }

    initSession()
  }, [])

  const handleVote = useCallback(
    async (winner: EloPhoto, loser: EloPhoto, side: 'A' | 'B') => {
      if (!sessionId || isSubmitting) return

      setSelectedPhoto(side)
      setIsSubmitting(true)
      setError(null)

      // Small delay for visual feedback
      await new Promise((resolve) => setTimeout(resolve, 300))

      const result = await submitEloVote(sessionId, winner.id, loser.id)

      if (result.error) {
        setError(result.error)
        setIsSubmitting(false)
        setSelectedPhoto(null)
        return
      }

      if (result.data) {
        setComparisonsCompleted(result.data.progress.completed)

        if (result.data.completed) {
          setIsComplete(true)
          // Navigate to success page after brief delay
          setTimeout(() => navigate('/vote/success'), 1500)
        } else if (result.data.photoA && result.data.photoB) {
          // Load next pair with animation
          setSelectedPhoto(null)
          setPhotoA(result.data.photoA)
          setPhotoB(result.data.photoB)
        }
      }

      setIsSubmitting(false)
    },
    [sessionId, isSubmitting, navigate]
  )

  if (isLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-accent mx-auto h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-foreground-muted mt-4">Loading photos...</p>
        </div>
      </div>
    )
  }

  if (error && !photoA) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md space-y-6 text-center"
        >
          <div className="bg-error/10 border-error/20 mx-auto flex h-16 w-16 items-center justify-center rounded-full border">
            <AlertCircle className="text-error h-8 w-8" />
          </div>
          <div className="space-y-2">
            <h1 className="text-foreground font-display text-2xl font-bold">Unable to Start Voting</h1>
            <p className="text-foreground-muted">{error}</p>
          </div>
          <Link to="/">
            <button className="bg-surface border-border text-foreground hover:bg-surface-hover rounded-xl border px-6 py-3 transition-colors">
              Back to Home
            </button>
          </Link>
        </motion.div>
      </div>
    )
  }

  if (isComplete) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md space-y-6 text-center"
        >
          <div className="bg-success/10 border-success/20 mx-auto flex h-20 w-20 items-center justify-center rounded-full border">
            <CheckCircle2 className="text-success h-10 w-10" />
          </div>
          <div className="space-y-2">
            <h1 className="text-foreground font-display text-3xl font-bold">All Done!</h1>
            <p className="text-foreground-muted">
              Thank you for voting! Your comparisons help determine the best photos.
            </p>
          </div>
        </motion.div>
      </div>
    )
  }

  const progressPercent = Math.round((comparisonsCompleted / comparisonsRequired) * 100)

  return (
    <div className="bg-background flex min-h-screen flex-col">
      {/* Header */}
      <div className="border-border bg-surface/80 border-b px-4 py-4 backdrop-blur-lg">
        <div className="mx-auto max-w-4xl">
          <Link
            to="/"
            className="text-foreground-muted hover:text-foreground mb-3 inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="text-foreground font-display text-2xl font-bold">Which Photo Is Better?</h1>
          <p className="text-foreground-muted mt-1 text-sm">Tap the photo you prefer. Quick decisions work best!</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="border-border bg-surface border-b px-4 py-3">
        <div className="mx-auto max-w-4xl">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-foreground-muted">Progress</span>
            <span className="text-foreground font-medium">
              {comparisonsCompleted} / {comparisonsRequired} comparisons
            </span>
          </div>
          <div className="bg-surface-hover h-2 overflow-hidden rounded-full">
            <motion.div
              className="bg-accent h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-3">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-error/10 border-error/20 text-error rounded-xl border p-3 text-center text-sm"
            >
              {error}
            </motion.div>
          </div>
        </div>
      )}

      {/* Photo Comparison */}
      <div className="flex flex-1 items-center justify-center px-4 py-6">
        <div className="w-full max-w-4xl">
          <div className="grid grid-cols-2 gap-3 md:gap-6">
            <AnimatePresence mode="wait">
              {photoA && (
                <PhotoCard
                  key={photoA.id}
                  photo={photoA}
                  onSelect={() => photoB && handleVote(photoA, photoB, 'A')}
                  isSelected={selectedPhoto === 'A'}
                  isSubmitting={isSubmitting}
                  disabled={isSubmitting}
                />
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {photoB && (
                <PhotoCard
                  key={photoB.id}
                  photo={photoB}
                  onSelect={() => photoA && handleVote(photoB, photoA, 'B')}
                  isSelected={selectedPhoto === 'B'}
                  isSubmitting={isSubmitting}
                  disabled={isSubmitting}
                />
              )}
            </AnimatePresence>
          </div>

          {/* VS Badge */}
          <div className="relative z-10 -mt-8 flex justify-center">
            <div className="bg-surface border-border flex h-14 w-14 items-center justify-center rounded-full border-2 shadow-lg">
              <span className="text-foreground-muted text-sm font-bold">VS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-4 text-center">
        <p className="text-foreground-subtle text-sm">
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Recording vote...
            </span>
          ) : (
            'Tap the photo you think is better'
          )}
        </p>
      </div>
    </div>
  )
}

interface PhotoCardProps {
  photo: EloPhoto
  onSelect: () => void
  isSelected: boolean
  isSubmitting: boolean
  disabled: boolean
}

function PhotoCard({ photo, onSelect, isSelected, isSubmitting, disabled }: PhotoCardProps) {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{
        opacity: 1,
        scale: isSelected ? 1.02 : 1,
        filter: isSubmitting && !isSelected ? 'brightness(0.7)' : 'brightness(1)',
      }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      onClick={onSelect}
      disabled={disabled}
      className={`bg-surface group relative aspect-[3/4] overflow-hidden rounded-2xl transition-all duration-200 focus:outline-none ${
        isSelected
          ? 'ring-accent ring-offset-background shadow-2xl ring-4 ring-offset-4'
          : 'hover:ring-foreground-subtle hover:ring-offset-background ring-0 hover:ring-2 hover:ring-offset-2'
      } ${disabled && !isSelected ? 'cursor-not-allowed' : 'cursor-pointer'} `}
    >
      <img
        src={photo.photoUrl}
        alt={photo.photoTitle}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="eager"
      />

      {/* Selection overlay */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-accent/20 absolute inset-0 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="bg-accent flex h-16 w-16 items-center justify-center rounded-full shadow-lg"
            >
              <CheckCircle2 className="text-accent-foreground h-8 w-8" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Title Overlay */}
      <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-4 pt-12">
        <p className="text-base font-medium text-white">{photo.photoTitle}</p>
        <p className="text-sm text-white/70">Grade {photo.grade}</p>
      </div>
    </motion.button>
  )
}
